import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, MoreVertical, Wine, ListChecks, Bell, FileText, Copy, Edit, Trash2, ChevronDown } from 'lucide-react';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/SubscriptionAppButton';
import { Card } from '../components/SubscriptionAppCard';
import { Input } from '../components/SubscriptionAppInput';
import { DropdownMenu } from '../components/SubscriptionAppDropdownMenu';
// Mock Data
interface Template {
  id: string;
  name: string;
  description: string;
  winesCount: number;
  rulesCount: number;
  notificationsCount: number;
  lastUsed: string;
}
const mockTemplates: Template[] = [{
  id: '1',
  name: 'Reserve Release Template',
  description: 'Standard template for single-wine reserve releases to top tier members',
  winesCount: 1,
  rulesCount: 3,
  notificationsCount: 3,
  lastUsed: '2 weeks ago'
}, {
  id: '2',
  name: 'Quarterly Library Collection',
  description: 'Multi-wine library release to all active subscribers with tiered quantity limits',
  winesCount: 4,
  rulesCount: 2,
  notificationsCount: 4,
  lastUsed: '3 months ago'
}, {
  id: '3',
  name: 'Vintner Exclusive',
  description: 'Small-batch release exclusively for Vintner tier members with high per-customer limits',
  winesCount: 1,
  rulesCount: 1,
  notificationsCount: 2,
  lastUsed: '1 month ago'
}, {
  id: '4',
  name: 'New Vintage Launch',
  description: 'Broad release for all members with no tier restrictions. Used for new vintage announcements.',
  winesCount: 6,
  rulesCount: 1,
  notificationsCount: 4,
  lastUsed: '6 months ago'
}];
export function AllocationTemplatesPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  // Filter templates based on search
  const filteredTemplates = mockTemplates.filter(template => template.name.toLowerCase().includes(searchQuery.toLowerCase()) || template.description.toLowerCase().includes(searchQuery.toLowerCase()));
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-6743" data-mp="3f6dzcxdikqif">
      {/* NavBar */}
      <div className="mb-6" data-id="element-6744" data-mp="3a5ghklwf1yhg">
        <NavBar tabs={[]} title="Allocation Templates" data-id="element-6745" data-mp="3a454fvh2zedo" />
      </div>

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-6746" data-mp="35757qb64fnz8">
        <h1 className="text-2xl font-bold text-[#0F172A] hidden sm:block" data-id="element-6747" data-mp="316urm5om7h6n">
          Allocation Templates
        </h1>
        <div className="flex items-center gap-3 w-full sm:w-auto justify-end" data-id="element-6748" data-mp="1rr3ekkc5dyk">
          <Button variant="primary" iconLeft={<Plus className="w-4 h-4" data-id="element-6750" data-mp="3o5hi208rr3we" />} onClick={() => console.log('Create Template')} data-id="element-6749" data-mp="3swzk6rmj3aiy">
            Create Template
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6" data-id="element-6751" data-mp="3k232faoqspjh">
        <div className="w-full sm:w-80" data-id="element-6752" data-mp="kiik676nk6mz">
          <Input placeholder="Search templates..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-6754" data-mp="1yd7c29zy34g0" />} data-id="element-6753" data-mp="fp5vxy5vy1ur" />
        </div>
        <div className="w-full sm:w-auto flex items-center gap-2" data-id="element-6755" data-mp="1yesawbtal9jo">
          <span className="text-sm text-[#64748B] whitespace-nowrap" data-id="element-6756" data-mp="23c26683d4fff">
            Sort by:
          </span>
          <button className="flex items-center gap-2 text-sm font-medium text-[#0F172A] bg-white border border-[#CBD5E1] rounded-md px-3 py-2 hover:bg-[#F8FAFC] transition-colors" data-id="element-6757" data-mp="27bsve7d8qnhu">
            Last Used
            <ChevronDown className="w-4 h-4 text-[#64748B]" data-id="element-6758" data-mp="2h97ao3nfh68k" />
          </button>
        </div>
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6759" data-mp="2r260founv2tr">
          {filteredTemplates.map(template => <Card key={template.id} padding="none" className="flex flex-col h-full border border-[#E2E8F0] hover:shadow-md transition-shadow duration-200" data-id="element-6760" data-mp="2v26pe3fdywf2">
              <div className="p-6 flex-1 flex flex-col" data-id="element-6761" data-mp="350cpcfhmtsha">
                <div className="flex items-start justify-between gap-4 mb-2" data-id="element-6762" data-mp="34yruspvbi9jf">
                  <h3 className="text-lg font-bold text-[#0F172A] line-clamp-1" data-id="element-6763" data-mp="2rckpyph2jqcx">
                    {template.name}
                  </h3>
                </div>
                <p className="text-sm text-[#64748B] line-clamp-2 mb-6 h-10" data-id="element-6764" data-mp="314i3i2vjuuet">
                  {template.description}
                </p>

                <div className="flex items-center gap-4 mb-6" data-id="element-6765" data-mp="2w5dvvgo9x4ql">
                  <div className="flex items-center gap-1.5 text-sm text-[#475569]" title={`${template.winesCount} wines`} data-id="element-6766" data-mp="28erpdttx3sip">
                    <Wine className="w-4 h-4 text-[#94A3B8]" data-id="element-6767" data-mp="23hhz6bypfctt" />
                    <span data-id="element-6768" data-mp="2id7bfie38blc">{template.winesCount}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-[#475569]" title={`${template.rulesCount} rules`} data-id="element-6769" data-mp="2ib4gql1zpuaa">
                    <ListChecks className="w-4 h-4 text-[#94A3B8]" data-id="element-6770" data-mp="3ts4gu2ukxdev" />
                    <span data-id="element-6771" data-mp="3tugw288yd9o8">{template.rulesCount}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-[#475569]" title={`${template.notificationsCount} notifications enabled`} data-id="element-6772" data-mp="3ot9ooaaq2ci3">
                    <Bell className="w-4 h-4 text-[#94A3B8]" data-id="element-6773" data-mp="57ah9wh1cemr" />
                    <span data-id="element-6774" data-mp="54oc23hmab7p">{template.notificationsCount}</span>
                  </div>
                </div>

                <div className="mt-auto pt-4 border-t border-[#F1F5F9] flex items-center justify-between" data-id="element-6775" data-mp="3neqh6uxuyvhn">
                  <span className="text-xs text-[#94A3B8]" data-id="element-6776" data-mp="iy5ufkykhhw5">
                    Last used: {template.lastUsed}
                  </span>
                </div>
              </div>

              <div className="p-4 bg-[#F8FAFC] border-t border-[#E2E8F0] flex items-center gap-3 rounded-b-lg" data-id="element-6777" data-mp="nwh7e4k5s8il">
                <Button variant="primary" size="sm" fullWidth onClick={() => navigate('/allocations/create')} data-id="element-6778" data-mp="a417hcmk0qlw">
                  Use Template
                </Button>
                <DropdownMenu items={[{
            label: 'Edit',
            icon: <Edit className="w-4 h-4" data-id="element-6780" data-mp="16o05py38myea" />,
            onClick: () => console.log('Edit', template.id)
          }, {
            label: 'Duplicate',
            icon: <Copy className="w-4 h-4" data-id="element-6781" data-mp="1ghmhi8fe8wce" />,
            onClick: () => console.log('Duplicate', template.id)
          }, {
            label: 'Delete',
            icon: <Trash2 className="w-4 h-4" data-id="element-6782" data-mp="17hzh9pyehwkx" />,
            variant: 'danger',
            onClick: () => console.log('Delete', template.id)
          }]} data-id="element-6779" data-mp="11vgsx3cdk7aj" />
              </div>
            </Card>)}
        </div> /* Empty State */ : <div className="flex flex-col items-center justify-center py-16 px-4 text-center bg-white rounded-lg border border-[#E2E8F0] border-dashed" data-id="element-6783" data-mp="xmfnb93ed7yt">
          <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mb-4" data-id="element-6784" data-mp="osb0r1vaqip3">
            <FileText className="w-8 h-8 text-[#94A3B8]" data-id="element-6785" data-mp="jtq051c4tx04" />
          </div>
          <h3 className="text-lg font-semibold text-[#0F172A] mb-2" data-id="element-6786" data-mp="jrmytl5j4hg3">
            No templates found
          </h3>
          <p className="text-[#64748B] max-w-md mb-6" data-id="element-6787" data-mp="9vaal2sr6vsq">
            {searchQuery ? `No templates match "${searchQuery}". Try a different search term.` : 'Save your first allocation as a template to reuse it later.'}
          </p>
          {!searchQuery && <button onClick={() => navigate('/allocations/create')} className="text-[#0EA5E9] font-medium hover:text-[#0284C7] hover:underline" data-id="element-6788" data-mp="4p51gba9v92x">
              Create Allocation
            </button>}
        </div>}
    </div>;
}